import AsyncSearchableSelect from "@/Components/AsyncSearchableSelect/AsyncSearchableSelect";
import { useAppSelector } from "@/Hooks/redux/useAppSelector";
import useDebounce from "@/Hooks/useDebounce/useDebounce";
import { useGetAppsQuery } from "@/Store/APIs/Apps.api";
import { useGetStatisticQuery } from "@/Store/APIs/Campaign.api";
import ApplicationStoreIcon from "@/UI/icons/ApplicationStoreIcon";
import { throwNewToastError } from "@/Utils/MainUtils";
import { ComboboxItem } from "@mantine/core";
import React, { useMemo, useState } from "react";

interface IApplicationSelectorProps {
  size: "sm" | "lg" | "md";
  placeholder: string;
  selectedApplication: number;
  handleSelectApplication: (appId: number) => void;
  disabled: boolean;
  inError?: boolean;
}

const StatisticApplicationSelector: React.FC<IApplicationSelectorProps> = ({
  size,
  placeholder,
  selectedApplication,
  handleSelectApplication,
  disabled,
  inError,
}) => {
  const [searchFieldValue, setSearchFieldValue] = useState("");

  const debouncedSearchValue = useDebounce(searchFieldValue, 500);

  const { data: allApplicationsList = [] } = useGetAppsQuery({
    limit: 999,
    offset: 0,
    search: null,
  });

  const { data: filteredApplicationsList = [] } = useGetAppsQuery({
    limit: 999,
    offset: 0,
    search: debouncedSearchValue,
  });

  const { dateStart, dateStop } = useAppSelector(
    (state) => state.myOrdersStatisticSlice
  );

  const { data: selectableAppsList = [] } = useGetStatisticQuery({
    dateStart,
    dateStop,
    apps: [],
  });

  const allSelectableApplicationsList = allApplicationsList.filter((app) =>
    selectableAppsList.some((active) => active.application === app.id)
  );

  const filteredSelectableApplicationsList = filteredApplicationsList.filter(
    (app) => selectableAppsList.some((active) => active.application === app.id)
  );

  const memoizedItemsList = useMemo(() => {
    return filteredSelectableApplicationsList.map((item) => ({
      label: item.name,
      value: String(item.id),
      store: item.url.includes("apple")
        ? ("apple" as const)
        : ("google" as const),
    }));
  }, [filteredSelectableApplicationsList]);

  const handleClear = () => {
    handleSelectApplication(0);
    setSearchFieldValue("");
  };

  const selectedApp: ComboboxItem | null = useMemo(() => {
    if (!selectedApplication || allSelectableApplicationsList.length === 0) {
      return null;
    }

    const app = allSelectableApplicationsList.filter(
      (app) => app.id === selectedApplication
    );

    return {
      label: app[0].name,
      value: String(app[0].id),
    };
  }, [selectedApplication, allSelectableApplicationsList]);

  return (
    <AsyncSearchableSelect
      data={memoizedItemsList}
      value={selectedApp}
      onClear={handleClear}
      onSearchChange={setSearchFieldValue}
      onChange={handleSelectApplication}
      searchValue={searchFieldValue}
      clearable
      placeholder={placeholder}
      width={size}
      error={inError}
      disabled={disabled}
      isSelectable={(item) => !item.label.includes("banned")}
      onInvalidSelect={() => throwNewToastError("Application has been banned")}
      renderOption={(item) => (
        <>
          <ApplicationStoreIcon store={item.store} />
          {item.label}
        </>
      )}
    />
  );
};

export default StatisticApplicationSelector;
